import moment from "moment";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineClose } from "react-icons/ai";
import { Store } from "react-notifications-component";
import DatePicker from "react-datepicker";
import { IoCalendar } from "react-icons/io5";
import { MemberShipDetails } from "./ClubProfileMembership";

export const MembershipModal = ({
    clubData,
    setOpenMembershipModal,
    setOpenMembershipPaymentDetails,
    formData,
    setFormData
}: {
    clubData: any;
    setOpenMembershipModal: Dispatch<SetStateAction<boolean>>;
    setOpenMembershipPaymentDetails: Dispatch<SetStateAction<boolean>>;
    formData: MemberShipDetails;
    setFormData: Dispatch<SetStateAction<MemberShipDetails>>;
}) => {
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const { t } = useTranslation();

    const handlePhoneNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const rawValue = event.target.value;
    
        // Remove all non-digit characters
        const numericValue = rawValue.replace(/[^\d]/g, '');
    
        const updatedFormData = {
            ...formData,
            Phone_number: numericValue,
        };
    
        setFormData(updatedFormData);
    
        const isValid = validateData(updatedFormData);
        if (!isValid) return;
    };

    const handleFormInputChange = (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            const { name, value } = event.target;
            setFormData({
                ...formData,
                [name]: value.replace(/^\s+/, "")
            });
            const isValid = validateData(formData);
            if (!isValid) return;
        };

    const validateData = (data: any) => {
        const errors: Record<string, string> = {};
    
        const phoneRegExp = /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/
        const dateRegExp = /^(?:19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
        const alphabeticRegex = /^[\p{L}\p{M}\s]+$/u;
    
        const required = t('required');
        const phoneShort = t('phoneShort');
        const personalShort = t('personalShort');
        const personalLong = t('personalLong');
        const phoneLong = t('phoneLong');            
        const onlyAlphabetAllowed = t('onlyAlphabetAllowed');
        const validPhoneMessage = t('validPhoneMessage');
        const validAddressMessage = t('validAddressMessage');
        const countryShort = t('countryShort');
        const countryLong = t('countryLong');
        const countyShort = t('countyShort');
        const countyLong = t('countyLong');
        const dobInvalid = t('dobInvalid');
        const mustBeThirteen = t('mustBeThirteen');
    
        // Date of Birth (if not SEK)
        if (clubData?.currency !== 'SEK') {
            const dob = formData.Date_of_birth;
    
             // If dob is a JavaScript Date object, convert it to a string
            let formattedDob = '';
            if (dob instanceof Date) {
                // Use moment to format Date to 'YYYY/MM/DD'
                formattedDob = moment(dob).format('YYYY/MM/DD');
            } else if (typeof dob === 'string') {
                // If dob is already a string, use it directly
                formattedDob = dob;
            }
            if (!formattedDob) {
                errors.Date_of_birth = required;
            } else if (!dateRegExp.test(formattedDob)) {
                errors.Date_of_birth = dobInvalid;
            } else {
                const dobDate = new Date(formattedDob);
                const today = new Date();
                const age = today.getFullYear() - dobDate.getFullYear();
                if (age < 13) {
                    errors.Date_of_birth = mustBeThirteen;
                }
            }
        }
    
        // Personal Number (if SEK)
        if (clubData?.currency === 'SEK') {
            const personalNumber = data.Personal_Number as string;
            if (!personalNumber) {
                errors.Personal_Number = required;
            } else if (personalNumber.length < 9) {
                errors.Personal_Number = personalShort;
            } else if (personalNumber.length > 20) {
                errors.Personal_Number = personalLong;
            }
        }
    
        // Phone Number
        const phone = data.Phone_number as string;
        if (!phone) {
            errors.Phone_number = required;
        } else if (phone.length < 9) {
            errors.Phone_number = phoneShort;
        } else if (phone.length > 20) {
            errors.Phone_number = phoneLong;
        } else if (!phoneRegExp.test(phone)) {
            errors.Phone_number = validPhoneMessage;
        }
    
        // Address
        const address = data.Address as string;
        if (!address) {
            errors.Address = required;
        } else if (!/[a-z]/i.test(address)) {
            errors.Address = validAddressMessage;
        }
    
        // Postal No
        const postal = data.Postal_no as string;
        if (!postal) {
            errors.Postal_no = required;
        }
    
        // County
        const county = data.County as string;
        if (!county) {
            errors.County = required;
        } else if (county.length < 2) {
            errors.County = countyShort;
        } else if (county.length > 15) {
            errors.County = countyLong;
        } else if (!alphabeticRegex.test(county)) {
            errors.County = onlyAlphabetAllowed;
        }
    
        // Country
        const country = data.Country as string;
        if (!country) {
            errors.Country = required;
        } else if (country.length < 2) {
            errors.Country = countryShort;
        } else if (country.length > 15) {
            errors.Country = countryLong;
        } else if (!alphabeticRegex.test(country)) {
            errors.Country = onlyAlphabetAllowed;
        }
    
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors); // ✅ Save errors in state
            return false; // prevent submit
        }
    
        // All good
        setFormErrors({}); // Clear previous errors
    
        // Proceed with valid data
        // Continue to payment or next step
        return true;
    };

    const handleDateChange = (date: any) => {
        setFormErrors({
            ...formErrors,
            Date_of_birth: date > new Date() ? t('dobInvalid') : ""
        });
        setFormData({
            ...formData,
            Date_of_birth: date
        });
    };

    const handleData = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // if values property's value is empty, then return
        const isSwedish =
        clubData?.currency === "SEK"
                ? !formData.Personal_Number
                : !formData.Date_of_birth;
        if (
            isSwedish ||
            !formData.Address ||
            !formData.Phone_number ||
            !formData.Postal_no ||
            !formData.County ||
            !formData.Country
            // || !dob
        ) {
            Store.addNotification({
                title: `${t("fillAllTheFields")}`,
                type: "danger",
                container: "bottom-center",
                animationIn: [
                    "animate__animated",
                    "animate__fadeIn"
                ],
                animationOut: [
                    "animate__animated",
                    "animate__fadeOut"
                ],
                dismiss: {
                    duration: 3000
                }
            });
            return;
        }

        const form = e.currentTarget;
        const formDataEntries = new FormData(form);
        
        const data = Object.fromEntries(formDataEntries.entries());
        const isValid = validateData(data);
        if (!isValid) return;

        setOpenMembershipModal(false);
        setOpenMembershipPaymentDetails(true);
    };

    return (
        <div className="bg-white font-semibold rounded w-full max-w-lg sm:min-w-[512px] mx-auto z-[5100]">
            <div className="text-white bg-secondary py-5 flex items-center justify-between flex-wrap gap-4 md:rounded-t text-2xl font-semibold px-5">
                <h4
                    id="becomeMember"
                    className="text-2xl lg:text-3xl font-semibold"
                >
                    {t("becomeMember")}
                </h4>
                <button
                    className="btn"
                    onClick={() => {
                        setOpenMembershipModal(false);
                    }}
                >
                    <AiOutlineClose className="pointer-events-none touch-none w-6 h-6 lg:w-8 lg:h-8 text-[#FF3A3A] hover:scale-110 transform duration-200" />
                </button>
            </div>
            <div className="overflow-y-auto p-5 relative">
                <form
                    className="flex flex-col gap-3"
                    onSubmit={handleData}
                >
                    {clubData?.currency === 'SEK' ? (<div>
                        <label
                            htmlFor="personalNumber"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("personalNumber")}
                        </label>
                        <input
                            id="personalNumber"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            placeholder={t("personalNumber")}
                            name="Personal_Number"
                            required
                            value={formData.Personal_Number}
                            onChange={handleFormInputChange}
                        />
                        {formErrors.Personal_Number && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Personal_Number}</p>
                        )}
                    </div>) : (
                    <>
                        <label
                            htmlFor="dateOfBirth"
                            className=" text-[#666B78] text-sm px-1 block -mb-2"
                        >
                            {t("dateOfBirth")}
                        </label>
                        <DatePicker
                            customInput={<div
                                id="dateOfBirth"
                                className="cursor-pointer w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary flex items-center justify-between"
                            >
                                <span id='#datePickerInputSpan'>{formData.Date_of_birth ? moment(formData.Date_of_birth).format('YYYY/MM/DD') : `${t("birthDate")} YYYY/MM/DD`}</span>
                                <IoCalendar size={22} className="text-black/60 cursor-pointer mr-2" />
                            </div>}
                            selected={formData.Date_of_birth ? moment(formData.Date_of_birth).toDate() : null}
                            onChange={handleDateChange}
                            maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 13))}
                            dateFormat="yyyy/MM/dd"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            placeholderText={`${t("dateOfBirth")} YYYY/MM/DD`}
                            isClearable // Allow the user to clear the date if needed
                        />
                        {formErrors.Date_of_birth && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Date_of_birth}</p>
                        )}
                    </>
                    )
                    }
                    <div>
                        <label
                            htmlFor="phoneNumber"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("phoneNumber")}
                        </label>
                        <input
                            id="phoneNumber"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            inputMode="numeric"             // shows numeric keyboard on mobile
                            pattern="[0-9]*"  
                            placeholder={t("phoneNumber")}
                            name="Phone_number"
                            required
                            value={formData.Phone_number}
                            onChange={handlePhoneNumberChange}
                        />
                        {formErrors.Phone_number && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Phone_number}</p>
                        )}
                    </div>   
                    <div>
                        <label
                            htmlFor="address"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("address")}
                        </label>
                        <input
                            id="address"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            placeholder={t("address")}
                            name="Address"
                            required
                            value={formData.Address}
                            onChange={handleFormInputChange}
                        />
                        {formErrors.Address && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Address}</p>
                        )}
                    </div>
                    <div>
                        <label
                            htmlFor="postalNo"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("postalNo")}
                        </label>
                        <input
                            id="postalNo"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            placeholder={t("postalNo")}
                            name="Postal_no"
                            required
                            value={formData.Postal_no}
                            onChange={handleFormInputChange}
                        />
                        {formErrors.Postal_no && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Postal_no}</p>
                        )}
                    </div>
                    <div>
                        <label
                            htmlFor="county"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("county")}
                        </label>
                        <input
                            id="county"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            placeholder={t("county")}
                            name="County"
                            required
                            value={formData.County}
                            onChange={handleFormInputChange}
                        />
                        {formErrors.County && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.County}</p>
                        )}
                    </div>
                    <div>
                        <label
                            htmlFor="country"
                            className=" text-[#666B78] text-sm px-1 mb-1 block"
                        >
                            {t("country")}
                        </label>
                        <input
                            id="country"
                            className="w-full h-11 border-[1px] border-[#666B78] rounded-[60px] text-secondary font-normal p-4 outline-primary"
                            type="text"
                            placeholder={t("country")}
                            name="Country"
                            required
                            value={formData.Country}
                            onChange={handleFormInputChange}
                        />
                        {formErrors.Country && (
                            <p className="text-red-500 text-sm mt-1 px-1">{formErrors.Country}</p>
                        )}
                    </div>
                    <div>
                        <input
                            type="submit"
                            value={t("BookingModal.continue")}
                            id="membershipFormSubmitButton"
                            className="cursor-pointer my-4 w-full h-11 border-[1px] border-secondary bg-secondary rounded-[60px] text-white font-normal flex justify-center items-center text-base"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};
